import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { useAppSettings, GraphSettings, DEFAULT_GRAPH_SETTINGS } from '@/hooks/useAppSettings';

interface AdminContextType {
  isAdmin: boolean;
  isAdminLoading: boolean;
  isEditMode: boolean;
  setEditMode: (value: boolean) => void;
  settings: GraphSettings;
  settingsLoading: boolean;
  saveSettings: (settings: GraphSettings) => Promise<boolean>;
  updateSetting: <K extends keyof GraphSettings>(key: K, value: GraphSettings[K]) => void;
  updateScentFamilyColor: (family: string, colors: { light: string; dark: string }) => void;
  resetToDefaults: () => void;
  isSettingsPanelOpen: boolean;
  setSettingsPanelOpen: (value: boolean) => void;
  // Manual sorting state
  isManualSortMode: boolean;
  setManualSortMode: (value: boolean) => void;
  manualSortSelectedTypes: Set<string>;
  toggleManualSortSelection: (typeId: string) => void;
  clearManualSortSelection: () => void;
  manualSortOrder: string[];
  setManualSortOrder: (order: string[], addToHistory?: boolean) => void;
  // Undo functionality for manual sorting
  canUndoManualSort: boolean;
  undoManualSort: () => void;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export function AdminProvider({ children }: { children: React.ReactNode }) {
  const { isAdmin, loading: isAdminLoading } = useAdmin();
  const { 
    settings, 
    loading: settingsLoading, 
    saveSettings, 
    updateSetting, 
    updateScentFamilyColor,
    resetToDefaults 
  } = useAppSettings();
  const [isEditMode, setEditMode] = useState(false);
  const [isSettingsPanelOpen, setSettingsPanelOpen] = useState(false);
  
  // Manual sorting state
  const [isManualSortMode, setManualSortMode] = useState(false);
  const [manualSortSelectedTypes, setManualSortSelectedTypes] = useState<Set<string>>(new Set());
  const [manualSortOrder, setManualSortOrderState] = useState<string[]>([]);
  const [manualSortHistory, setManualSortHistory] = useState<string[][]>([]);
  
  // Wrapper to add undo history
  const setManualSortOrder = useCallback((order: string[], addToHistory = true) => {
    if (addToHistory) {
      setManualSortHistory(prev => [...prev, manualSortOrder]);
    }
    setManualSortOrderState(order);
  }, [manualSortOrder]);
  
  const canUndoManualSort = manualSortHistory.length > 0;
  
  const undoManualSort = useCallback(() => {
    if (manualSortHistory.length > 0) {
      const newHistory = [...manualSortHistory];
      const previousOrder = newHistory.pop()!;
      setManualSortHistory(newHistory);
      setManualSortOrderState(previousOrder);
    }
  }, [manualSortHistory]);

  const toggleManualSortSelection = useCallback((typeId: string) => {
    setManualSortSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(typeId)) {
        next.delete(typeId);
      } else {
        next.add(typeId);
      }
      return next;
    });
  }, []);

  const clearManualSortSelection = useCallback(() => {
    setManualSortSelectedTypes(new Set());
  }, []);

  // Reset edit mode if user loses admin status
  React.useEffect(() => {
    if (!isAdmin && isEditMode) {
      setEditMode(false);
      setManualSortMode(false);
    }
  }, [isAdmin, isEditMode]);

  // Reset manual sort mode when exiting edit mode
  React.useEffect(() => {
    if (!isEditMode && isManualSortMode) {
      setManualSortMode(false);
      setManualSortSelectedTypes(new Set());
      setManualSortHistory([]);
    }
  }, [isEditMode, isManualSortMode]);

  return (
    <AdminContext.Provider
      value={{
        isAdmin,
        isAdminLoading,
        isEditMode,
        setEditMode,
        settings,
        settingsLoading,
        saveSettings,
        updateSetting,
        updateScentFamilyColor,
        resetToDefaults,
        isSettingsPanelOpen,
        setSettingsPanelOpen,
        // Manual sorting
        isManualSortMode,
        setManualSortMode,
        manualSortSelectedTypes,
        toggleManualSortSelection,
        clearManualSortSelection,
        manualSortOrder,
        setManualSortOrder,
        canUndoManualSort,
        undoManualSort,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
}

export function useAdminContext() {
  const context = useContext(AdminContext);
  if (context === undefined) {
    // During HMR or edge cases, return safe defaults instead of throwing
    return {
      isAdmin: false,
      isAdminLoading: true,
      isEditMode: false,
      setEditMode: () => {},
      settings: {} as GraphSettings,
      settingsLoading: true,
      saveSettings: async () => false,
      updateSetting: () => {},
      updateScentFamilyColor: () => {},
      resetToDefaults: () => {},
      isSettingsPanelOpen: false,
      setSettingsPanelOpen: () => {},
      // Manual sorting defaults
      isManualSortMode: false,
      setManualSortMode: () => {},
      manualSortSelectedTypes: new Set<string>(),
      toggleManualSortSelection: () => {},
      clearManualSortSelection: () => {},
      manualSortOrder: [] as string[],
      setManualSortOrder: () => {},
      canUndoManualSort: false,
      undoManualSort: () => {},
    };
  }
  return context;
}
